import React, { useState, forwardRef, useId } from 'react';
import { EyeIcon, EyeOffIcon, CheckCircleIcon, AlertCircleIcon } from 'lucide-react';
interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  icon?: React.ReactNode;
  success?: boolean;
  /** Floating label style (label animates above on focus/fill) */
  floating?: boolean;
}
export const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  helperText,
  icon,
  success,
  floating = false,
  className = '',
  id,
  type,
  value,
  defaultValue,
  onFocus,
  onBlur,
  ...props
}, ref) => {
  const generatedId = useId();
  const inputId = id || generatedId;
  const isPasswordType = type === 'password';
  const [showPassword, setShowPassword] = useState(false);
  const [focused, setFocused] = useState(false);
  const resolvedType = isPasswordType && showPassword ? 'text' : type;

  // Determine if floating label should be "raised"
  const hasValue = value !== undefined ? String(value).length > 0 : defaultValue !== undefined ? String(defaultValue).length > 0 : false;
  const labelUp = floating && (focused || hasValue);
  const borderClass = error ? 'border-red-400 dark:border-red-500 focus:ring-red-400/40' : success ? 'border-emerald-400 dark:border-emerald-500 focus:ring-emerald-400/40' : 'border-gray-200 dark:border-white/[0.12] focus:ring-cyan-500/30 focus:border-cyan-400 dark:focus:border-cyan-500';
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(true);
    onFocus?.(e);
  };
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(false);
    onBlur?.(e);
  };
  return <div className={`w-full ${error ? 'animate-shake' : ''}`} style={{
    animationIterationCount: 1
  }} data-id="element-5427">
        {/* Non-floating label */}
        {label && !floating && <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5428">
          
            {label}
          </label>}

        <div className="relative" data-id="element-5429">
          {/* Floating label */}
          {label && floating && <label htmlFor={inputId} className={`
                absolute left-3 pointer-events-none z-10 transition-all duration-200 origin-left
                ${icon ? 'left-10' : 'left-3.5'}
                ${labelUp ? '-top-2.5 text-[11px] font-semibold bg-white dark:bg-gray-900 px-1 rounded text-cyan-500 dark:text-cyan-400' : 'top-1/2 -translate-y-1/2 text-sm text-gray-400 dark:text-gray-500'}
              `} data-id="element-5430">
            
              {label}
            </label>}

          {/* Left icon */}
          {icon && <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 dark:text-gray-500" data-id="element-5431">
              {icon}
            </div>}

          <input ref={ref} id={inputId} type={resolvedType} value={value} defaultValue={defaultValue} onFocus={handleFocus} onBlur={handleBlur} className={`
              w-full rounded-xl border bg-white dark:bg-white/[0.07]
              text-gray-900 dark:text-gray-100
              placeholder-gray-400 dark:placeholder-gray-300
              transition-all duration-200
              focus:outline-none focus:ring-2
              disabled:bg-gray-50 disabled:cursor-not-allowed dark:disabled:bg-white/[0.02]
              disabled:text-gray-400 dark:disabled:text-gray-600
              py-2.5 text-sm
              ${borderClass}
              pl-3.5
              ${isPasswordType || success || error ? 'pr-10' : 'pr-3.5'}
              ${floating ? 'pt-3.5 pb-1.5' : ''}
              ${className}
            `} {...props} data-id="element-5432" />
          

          {/* Right status / password toggle */}
          {isPasswordType ? <button type="button" tabIndex={-1} onClick={() => setShowPassword(p => !p)} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" title={showPassword ? 'Ocultar' : 'Mostrar'} data-id="element-5433">
            
              {showPassword ? <EyeOffIcon className="w-4 h-4" data-id="element-5434" /> : <EyeIcon className="w-4 h-4" data-id="element-5435" />}
            </button> : error ? <div className="absolute right-3 top-1/2 -translate-y-1/2 text-red-400 pointer-events-none" data-id="element-5436">
              <AlertCircleIcon className="w-4 h-4" data-id="element-5437" />
            </div> : success ? <div className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-500 pointer-events-none" data-id="element-5438">
              <CheckCircleIcon className="w-4 h-4" data-id="element-5439" />
            </div> : null}
        </div>

        {/* Messages */}
        {error && <p className="mt-1.5 text-xs text-red-500 dark:text-red-400 flex items-center gap-1 animate-fade-in" data-id="element-5440">
            {error}
          </p>}
        {helperText && !error && <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400" data-id="element-5441">{helperText}</p>}
      </div>;
});
Input.displayName = 'Input';